import { useQuery } from "@apollo/client";
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  type BulkManifestDiscrepanciesQuery,
  DiscrepancyKind,
  DiscrepancyStatus,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useMemo, useState } from "react";

import {
  type ComplianceItemEpa,
  epaCorrectionSectionText,
  mapEpaCorrectionDiscrepancy,
} from "./CompliancePane.helpers";
import { BULK_MANIFEST_DISCREPANCIES_QUERY } from "./DiscrepancyReviewDialog";
import {
  formatDisplayValue,
  getDiscrepancyStatus,
  getFieldLabel,
  getStatusColor,
} from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancySection } from "./DiscrepancySection";
import { DiscrepancySummaryBar } from "./DiscrepancySummaryBar";
import { SOURCE_LABEL } from "./manifestSourceDisplay";
import { buildDiscrepancyBreakdown } from "./sections/discrepancyBreakdown";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];
type CustomerSectionKey = "epaCorrection" | "field";

interface CustomerDiscrepancyDialogProps {
  processedManifestId: string;
  manifestTrackingNumber?: string | null;
  onClose: () => void;
}

const SECTION_LABELS: Record<CustomerSectionKey, string> = {
  epaCorrection: "EPA-Requested Corrections",
  field: "Data Source Conflicts",
};

function customerDiscrepancies(
  discrepancies: readonly Discrepancy[],
  showEpaCorrections: boolean
): Discrepancy[] {
  return discrepancies.filter((discrepancy) => {
    if (discrepancy.status === DiscrepancyStatus.Ignored) return false;
    if (discrepancy.kind === DiscrepancyKind.FieldDiscrepancy) {
      return (
        discrepancy.status === DiscrepancyStatus.Pending ||
        discrepancy.status === DiscrepancyStatus.Resolved
      );
    }
    return showEpaCorrections && discrepancy.kind === DiscrepancyKind.EpaCorrectionRequest;
  });
}

function correctionSummary(
  epaItems: readonly ComplianceItemEpa[],
  fieldDiscrepancies: readonly Discrepancy[]
): string {
  // EPA section/comment text is the card title — keep the subheader as a
  // pointer, not a second copy of the same § / quote string.
  const fieldLabels = [...new Set(fieldDiscrepancies.map((item) => getFieldLabel(item.field)))];
  if (epaItems.length > 0 && fieldLabels.length === 0) {
    return epaItems.length === 1
      ? "Review the EPA correction request below."
      : `Review ${epaItems.length} EPA correction requests below.`;
  }
  if (fieldLabels.length === 1 && epaItems.length === 0) {
    return `Data conflict: ${fieldLabels[0]}`;
  }
  if (fieldLabels.length > 1 && epaItems.length === 0) {
    return `Data conflicts: ${fieldLabels.join(", ")}`;
  }
  if (epaItems.length > 0 && fieldLabels.length > 0) {
    return "Review the EPA corrections and data conflicts below.";
  }
  return "Review the correction details below.";
}

function CustomerStatusGlossary() {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" component="h2" sx={{ mb: 1 }}>
        What these statuses mean
      </Typography>
      <Stack spacing={0.5}>
        <Typography variant="body2">
          <strong>Critical:</strong> EPA requested a correction that needs prompt attention.
        </Typography>
        <Typography variant="body2">
          <strong>Pending:</strong> An EPA correction is in progress and awaiting refreshed data.
        </Typography>
        <Typography variant="body2">
          <strong>Fix pending:</strong> Encamp is correcting a conflict between manifest data
          sources.
        </Typography>
        <Typography variant="body2">
          <strong>Resolved:</strong> The corrected data is now reflected in the manifest.
        </Typography>
      </Stack>
    </Paper>
  );
}

function SourceValue({ label, value }: { label: string; value?: string | null }) {
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 112 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {formatDisplayValue(value)}
      </Typography>
    </Stack>
  );
}

function FieldDiscrepancyCard({ discrepancy }: { discrepancy: Discrepancy }) {
  const status = getDiscrepancyStatus(discrepancy);
  const statusLabel = status === "pending" ? "Fix pending" : "Resolved";
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="subtitle2" component="h3">
            {getFieldLabel(discrepancy.field)}
          </Typography>
          {discrepancy.wasteLineUnifiedId && (
            <Typography variant="caption" color="text.secondary">
              Waste line {discrepancy.wasteLineNumber ?? "item"}
            </Typography>
          )}
        </Box>
        <Chip size="small" label={statusLabel} color={getStatusColor(status)} variant="outlined" />
      </Stack>
      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        <SourceValue label="e-Manifest" value={discrepancy.eManifestValue} />
        <SourceValue label="Vendor" value={discrepancy.vendorValue} />
        <SourceValue
          label={SOURCE_LABEL[WasteManifestSource.ManifestDocument]}
          value={discrepancy.manifestDocumentValue}
        />
      </Stack>
    </Paper>
  );
}

function EpaCorrectionCard({ item }: { item: ComplianceItemEpa }) {
  let statusLabel = "Critical";
  if (item.status === "pending") statusLabel = "Pending";
  if (item.status === "resolved") statusLabel = "Resolved";
  const title = epaCorrectionSectionText(item) ?? "EPA correction request";
  const extraSections = item.sections.slice(1);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Typography variant="subtitle2" component="h3" sx={{ wordBreak: "break-word" }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={statusLabel}
          color={getStatusColor(item.status)}
          variant="outlined"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Requested {item.requestedAt} by {item.initiator} · response due {item.deadline}
      </Typography>
      {extraSections.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {extraSections.map((section, index) => (
            <Typography
              key={`${section.sectionNumber}-${index}`}
              variant="body2"
              sx={{ wordBreak: "break-word" }}
            >
              <strong>§ {section.sectionNumber}</strong> – &quot;{section.comments}&quot;
            </Typography>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export function CustomerDiscrepancyDialog({
  processedManifestId,
  manifestTrackingNumber,
  onClose,
}: CustomerDiscrepancyDialogProps) {
  const { featureFlags } = useFeatureFlags();
  const showEpaCorrections = !!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];
  const { data, loading } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds: [processedManifestId] },
  });
  const [activeSection, setActiveSection] = useState<CustomerSectionKey | null>(null);

  const visibleDiscrepancies = useMemo(
    () => customerDiscrepancies(data?.bulkManifestDiscrepancies ?? [], showEpaCorrections),
    [data?.bulkManifestDiscrepancies, showEpaCorrections]
  );
  const fieldDiscrepancies = useMemo(
    () =>
      visibleDiscrepancies.filter(
        (discrepancy) => discrepancy.kind === DiscrepancyKind.FieldDiscrepancy
      ),
    [visibleDiscrepancies]
  );
  const epaItems = useMemo(() => {
    const items: ComplianceItemEpa[] = [];
    for (const discrepancy of visibleDiscrepancies) {
      if (discrepancy.kind !== DiscrepancyKind.EpaCorrectionRequest) continue;
      const item = mapEpaCorrectionDiscrepancy({
        ...discrepancy,
        status: getDiscrepancyStatus(discrepancy),
      });
      if (item) items.push(item);
    }
    return items;
  }, [visibleDiscrepancies]);

  const breakdown = useMemo(
    () => buildDiscrepancyBreakdown(visibleDiscrepancies),
    [visibleDiscrepancies]
  );
  const countFor = (key: CustomerSectionKey) =>
    breakdown.rows.find((row) => row.key === key)?.counts.total ?? 0;
  const availableSections: CustomerSectionKey[] = [];
  if (epaItems.length > 0) availableSections.push("epaCorrection");
  if (fieldDiscrepancies.length > 0) availableSections.push("field");
  const summaryTiles = availableSections.map((key) => ({
    key,
    label: SECTION_LABELS[key],
    count: countFor(key),
  }));
  const showSection = (key: CustomerSectionKey) => activeSection == null || activeSection === key;
  const titleSuffix = manifestTrackingNumber ?? processedManifestId.slice(0, 8);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={`Manifest discrepancies — #${titleSuffix}`}
      subheader={correctionSummary(epaItems, fieldDiscrepancies)}
      hideCancelButton
      primaryActionButton={
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      }
    >
      <CustomerStatusGlossary />
      {loading && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      )}
      {!loading && availableSections.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No pending or resolved discrepancies.
        </Typography>
      )}
      {!loading && availableSections.length > 0 && (
        <>
          <DiscrepancySummaryBar
            tiles={summaryTiles}
            loading={false}
            activeKeys={activeSection ? [activeSection] : []}
            onTileClick={(key) => setActiveSection((current) => (current === key ? null : key))}
          />
          {showSection("epaCorrection") && epaItems.length > 0 && (
            <DiscrepancySection title="EPA-Requested Corrections" count={epaItems.length}>
              <Stack spacing={1.5} sx={{ p: 1.5 }}>
                {epaItems.map((item) => (
                  <EpaCorrectionCard key={item.id} item={item} />
                ))}
              </Stack>
            </DiscrepancySection>
          )}
          {showSection("field") && fieldDiscrepancies.length > 0 && (
            <DiscrepancySection title="Data Source Conflicts" count={fieldDiscrepancies.length}>
              <Stack spacing={1.5} sx={{ p: 1.5 }}>
                {fieldDiscrepancies.map((discrepancy) => (
                  <FieldDiscrepancyCard key={discrepancy.id} discrepancy={discrepancy} />
                ))}
              </Stack>
            </DiscrepancySection>
          )}
        </>
      )}
    </Dialog>
  );
}
